import { useState, useEffect } from 'react';
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';

import { svgToOutput } from '@clear-coat/mo_svg/src';
import { SVGPreview } from '../SVGPreview';
import { bumpModelVersion, detectFill, getSVGInfo } from '@utils/designs';
import { designAPI } from '@api/designs';
import { useSelector } from 'react-redux';
import { useStyles } from '../styles/DialogStyles.styles';
import { FileUploader } from 'react-drag-drop-files';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { useAlert } from '@hooks/useAlert';
import { System } from '@consts/systems';

import type { ShapeInfo } from '@utils/designs';
import type { Coverage, Model } from '@features/redux/slices/designSlice';
import type { Dictionary } from '@/types/general';
import type { RootState } from '@features/redux/store';
import type { Shapes } from '@clear-coat/mo_svg/src';

const ALERT_TIMEOUT = 10000;

interface DesignEditDialogProps {
    model: Model;
    selectedCoverageId?: string;
    coverageMap: Dictionary<Coverage>;
    cancel: (updated: boolean) => void;
}

export const DesignEditDialog = ({ model, selectedCoverageId, coverageMap, cancel }: DesignEditDialogProps) => {
    const styles = useStyles();

    const { IdToken } = useSelector((state: RootState) => state.auth);

    const { postModel } = designAPI(IdToken as string);

    const { setAlert } = useAlert();

    const selectedCoverage = selectedCoverageId !== undefined ? coverageMap[selectedCoverageId] : undefined;

    const [coverage, setCoverage] = useState<Coverage>();
    const [svg, setSVG] = useState<string>();
    const [fillError, setFillError] = useState<boolean>(false);
    const [system, setSystem] = useState<System[]>([]);

    const [shapes, setShapes] = useState<Shapes>([]);
    const [shapeInfo, setShapeInfo] = useState<ShapeInfo>();

    useEffect(() => {
        if (selectedCoverageId !== undefined) {
            const defaultSVG = model.coverages[selectedCoverageId];
            shapeData(defaultSVG);
            setSVG(defaultSVG);
            setCoverage(selectedCoverage);
        }
    }, [selectedCoverageId]);

    const shapeData = (svg: string, filename?: string) => {
        try {
            const shapes = svgToOutput(svg, {
                resetToOrigin: { properties: {} },
                reflect: { properties: { axis: 'x' } },
            });

            const info = getSVGInfo(shapes);

            setFillError(detectFill(svg));
            setShapeInfo(info);
            setShapes(shapes);

            if (filename) {
                setAlert(`Successfully Uploaded Design from file: ${filename}`, 'success', ALERT_TIMEOUT);
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Malformed Design found', err);
            if (filename) {
                setAlert(`Failed to Uploaded Design from file: ${filename}`, 'error', ALERT_TIMEOUT);
            }
        }
    };

    const saveDesign = async () => {
        if (!svg || coverage === undefined) {
            alert('Fill in all data!');
        } else {
            const coverageCopy = { ...(model?.coverages || {}) };

            if (selectedCoverage) {
                delete coverageCopy[selectedCoverage.coverageId];
            }

            const newModel: Model = {
                ...model,
                version: bumpModelVersion(model?.version),
                coverages: {
                    ...coverageCopy,
                    [(coverage as Coverage).coverageId]: svg,
                },
            };

            try {
                await postModel(newModel);
                cancel(true);
            } catch (error) {
                alert('Failed to save design!');
            }
        }
    };

    const handleUpload = async (file: File) => {
        const data = await file.text();
        shapeData(data, file.name);
        setSVG(data);
    };

    const isCompatible = (coverage: Coverage) => {
        const compatability = (coverage.compatability || '').split(',');
        return compatability.length && system.length ? system.every((item) => compatability.includes(item)) : false;
    };

    return (
        <Dialog open={true} fullWidth maxWidth={'md'}>
            <DialogTitle className={styles.dialogTitle}>{selectedCoverage ? 'Edit Design' : 'Add Design'}</DialogTitle>

            <DialogContent className={styles.dialogContent}>
                <Typography style={{ marginLeft: '5px' }} variant='h5'>
                    {model?.model}
                </Typography>

                <Autocomplete
                    disablePortal
                    multiple
                    options={Object.values(System)}
                    className={styles.textField}
                    value={system}
                    onChange={(_, value) => {
                        setSystem(value);
                        setCoverage(undefined);
                    }}
                    renderInput={(params) => <TextField {...params} label='System' />}
                />

                <Autocomplete
                    disablePortal
                    disabled={system.length === 0}
                    options={Object.values(coverageMap).filter((coverage) => coverage.active && isCompatible(coverage))}
                    getOptionLabel={(option) => option.coverageName}
                    className={styles.textField}
                    value={(coverage || null) as Coverage}
                    isOptionEqualToValue={(option, value) => option.coverageId === value.coverageId}
                    onChange={(_, value) => setCoverage(value as Coverage)}
                    renderInput={(params) => <TextField {...params} label='Coverage' />}
                />

                <div className={styles.uploadContainer}>
                    <FileUploader handleChange={handleUpload} types={['SVG']} name='file' hoverTitle={'Drop here'}>
                        <div className={styles.uploadButton}>
                            <AddPhotoAlternateOutlinedIcon className={styles.uploadIcon} />
                            {svg ? <u>Design loaded.</u> : null} &nbsp;{' '}
                            {svg ? 'Drag or click to upload a different one.' : 'Drag or click to upload new design'}
                        </div>
                    </FileUploader>
                </div>

                {svg && shapes.length === 0 && (
                    <Typography color={'red'} style={{ marginTop: '20px' }}>
                        Design is malformed!
                    </Typography>
                )}

                {shapeInfo && (
                    <div style={{ marginTop: '20px' }}>
                        {shapeInfo.unclosedCount > 0 && (
                            <Typography color={'red'}>Found unclosed paths in the design!</Typography>
                        )}

                        {shapeInfo.closeCount > 0 && (
                            <Typography color={'orangered'}>Found potential path errors in the design!</Typography>
                        )}

                        {fillError && <Typography color={'orangered'}>Found a fill included in the design!</Typography>}

                        {shapeInfo.unclosedCount === 0 && shapeInfo.closeCount === 0 && !fillError && (
                            <Typography color={'green'}>Found no issues in design!</Typography>
                        )}
                    </div>
                )}

                {shapes.length > 0 && shapeInfo && (
                    <div
                        style={{
                            position: 'absolute',
                            right: '10px',
                            bottom: '0px',
                        }}>
                        <SVGPreview svgShapes={shapes} highlightIndexes={shapeInfo.highlightIndexes} />
                    </div>
                )}
            </DialogContent>

            <DialogActions className={styles.dialogActions}>
                <Button
                    disabled={!coverage || !svg}
                    variant={'contained'}
                    className={styles.save}
                    onClick={() => saveDesign()}>
                    Save
                </Button>
                <Button onClick={() => cancel(false)}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
};
